import { graphqlQuery } from "@/lib/api/graphql";
import type { PromiseAuditLog } from "@/types/collections";
import type { GraphQLCursorConnection } from "@/lib/graphql-pagination";
import { useCursorPagination } from "./useCursorPagination";

// --- GraphQL types ---

interface PromiseAuditLogGraphQLNode {
	auditLogId: number;
	promiseId: number;
	actionType: string | null;
	description: string;
	fieldName: string | null;
	oldValue: string | null;
	newValue: string | null;
	metadata: string | null;
	createdByUserName: string | null;
	createdAt: string;
}

interface PromiseAuditLogListGraphQLResponse {
	promisesToPay: {
		promiseAuditLogList: GraphQLCursorConnection<PromiseAuditLogGraphQLNode>;
	};
}

// --- GraphQL query ---

const PROMISE_AUDIT_LOG_LIST_QUERY = `
  query PromiseAuditLogList($first: Int, $after: String, $filter: PromiseAuditLogListFilterInput) {
    promisesToPay {
      promiseAuditLogList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            auditLogId
            promiseId
            actionType
            description
            fieldName
            oldValue
            newValue
            metadata
            createdByUserName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const AUDIT_LOG_PAGE_SIZE = 25;

// --- Mapping ---

const actionTypeToActionMap: Record<string, PromiseAuditLog["action"]> = {
	promisecreated: "promise_created",
	statuschanged: "status_change",
	amountupdated: "amount_change",
	dateupdated: "date_change",
	confirmationmethodupdated: "field_update",
	noteadded: "note_added",
	casereassigned: "collection_reassignment",
	assigneduserupdated: "assigned_user_updated",
	transactionlinked: "transaction_linked",
	transactionunlinked: "transaction_unlinked",
	phonecalllogged: "phone_call_logged",
};

function mapGraphQLNodeToPromiseAuditLog(
	node: PromiseAuditLogGraphQLNode,
): PromiseAuditLog {
	const actionName = (node.actionType ?? "").toLowerCase().replace(/\s+/g, "");
	const log: PromiseAuditLog = {
		id: String(node.auditLogId),
		promiseId: String(node.promiseId),
		action: actionTypeToActionMap[actionName] || "field_update",
		description: node.description,
		fieldName: node.fieldName || undefined,
		oldValue: node.oldValue,
		newValue: node.newValue,
		createdBy: node.createdByUserName || "System",
		createdAt: new Date(node.createdAt),
	};

	// Parse metadata for collection_reassignment fields
	if (node.metadata) {
		try {
			const metadata = JSON.parse(node.metadata) as Record<string, unknown>;
			if (typeof metadata.oldCaseId === "string")
				log.oldCaseId = metadata.oldCaseId;
			if (typeof metadata.newCaseId === "string")
				log.newCaseId = metadata.newCaseId;
			if (typeof metadata.oldCaseNumber === "string")
				log.oldCaseNumber = metadata.oldCaseNumber;
			if (typeof metadata.newCaseNumber === "string")
				log.newCaseNumber = metadata.newCaseNumber;
		} catch {
			// ignore malformed metadata
		}
	}

	return log;
}

// --- Mock data builder ---

function buildMockPromiseAuditLogs(
	promiseId: string,
	promisedAmount: number,
): PromiseAuditLog[] {
	return [
		{
			id: "audit-1",
			promiseId,
			action: "promise_created",
			description: "Promise to pay created",
			createdBy: "System",
			createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-2",
			promiseId,
			action: "status_change",
			description: "Promise status changed",
			fieldName: "status",
			oldValue: "Active",
			newValue: "Promised",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-3",
			promiseId,
			action: "amount_change",
			description: "Promised amount updated",
			fieldName: "promisedAmount",
			oldValue: promisedAmount - 500,
			newValue: promisedAmount,
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-4",
			promiseId,
			action: "date_change",
			description: "Promise date updated",
			fieldName: "promiseDate",
			oldValue: "Jan 01, 2024",
			newValue: "Jan 08, 2024",
			createdBy: "Mike Chen",
			createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-5",
			promiseId,
			action: "field_update",
			description: "Confirmation method updated",
			fieldName: "confirmationMethod",
			oldValue: "Email",
			newValue: "Phone",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-6",
			promiseId,
			action: "note_added",
			description: "Note added to promise",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
		},
		{
			id: "audit-7",
			promiseId,
			action: "assigned_user_updated",
			description: "Promise reassigned",
			fieldName: "assignedUser",
			oldValue: "John Smith",
			newValue: "Sarah Johnson",
			createdBy: "System",
			createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
		},
	];
}

// --- Hook ---

export interface PromiseAuditLogsGraphQLResult {
	auditLogs: PromiseAuditLog[];
	isLoading: boolean;
	isFetchingMore: boolean;
	hasMore: boolean;
	loadMore: () => void;
	reset: () => void;
}

export function usePromiseAuditLogsGraphQL(
	promiseId: string | undefined,
	promisedAmount: number,
): PromiseAuditLogsGraphQLResult {
	const result = useCursorPagination<PromiseAuditLogGraphQLNode, PromiseAuditLog>(
		{
			pageSize: AUDIT_LOG_PAGE_SIZE,
			buildMockData: () =>
				promiseId ? buildMockPromiseAuditLogs(promiseId, promisedAmount) : [],
			executeQuery: async (apiConfig, afterCursor, signal) => {
				if (!promiseId) {
					return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
				}
				const variables: Record<string, unknown> = {
					first: AUDIT_LOG_PAGE_SIZE,
					filter: { promiseId: { eq: Number(promiseId) } },
				};
				if (afterCursor) {
					variables.after = afterCursor;
				}
				const response = await graphqlQuery<PromiseAuditLogListGraphQLResponse>(
					apiConfig,
					PROMISE_AUDIT_LOG_LIST_QUERY,
					variables,
					signal,
				);
				return response.promisesToPay.promiseAuditLogList;
			},
			mapNode: mapGraphQLNodeToPromiseAuditLog,
		},
		[promiseId, promisedAmount],
	);

	return {
		auditLogs: result.items,
		isLoading: result.isLoading,
		isFetchingMore: result.isFetchingMore,
		hasMore: result.hasMore,
		loadMore: result.loadMore,
		reset: result.reset,
	};
}
